import { useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isEmpty, sortBy } from 'lodash'
import Fuse from 'fuse.js'
import { useSelector } from 'react-redux'

import Loader from 'components/Loader'
import Layout from 'components/Layout'

import { getCurrentOrgName } from 'store/appStateSlice/selectors'
import { useGetPosAccountsQuery } from 'store/stadiumApi/posAccounts'
import { useGetEmployeeRolesQuery } from 'store/stadiumApi/employeeRoles'
import PosAccountsLayout from 'views/PosAccounts/Layout/PosAccountsLayout'
import PosAccountsFilters from 'views/PosAccounts/Filters/PosAccountsFilters'
import paginationCoordinator from 'utils/general/paginationCoordinator'
import { extendFuseOptions } from 'utils/search/extendFuseOptions'
import { filterPosAccountsByStatus } from 'utils/posAccounts/filterPosAccountsByStatus'
import {
  TPosAccount,
  TPosAccountRole,
  IPosAccountWithRoles,
  TEmployeeRolesQuery,
} from 'types/posAccounts'
import { filterPosAccountsByRole } from 'utils/posAccounts/filterPosAccountsByRole'

const PosAccounts = () => {
  const [statusQuery, setSelectStatusQuery] = useState('')
  const [rolesQuery, setRolesQuery] = useState<TEmployeeRolesQuery>('all-roles')
  const [query, setQuery] = useState('')

  const orgName = useSelector(getCurrentOrgName)

  const { data: posAccounts, isFetching: isFetchingAccounts } = useGetPosAccountsQuery({ orgName })
  const { data: employeeRole, isFetching: isFetchingEmployeeRoles } = useGetEmployeeRolesQuery()

  const location = useLocation()
  const navigate = useNavigate()

  const accountsSortedByName = sortBy(posAccounts, 'name')

  const filteredAccountsByStatus = accountsSortedByName
    ? filterPosAccountsByStatus(statusQuery, accountsSortedByName)
    : []

  const filteredAccountsByRole =
    useMemo(
      () =>
        filterPosAccountsByRole(
          filteredAccountsByStatus as TPosAccount[],
          employeeRole?.employeeRoles as TPosAccountRole[],
          rolesQuery
        ),
      [filteredAccountsByStatus, employeeRole?.employeeRoles, rolesQuery]
    ) ?? []

  const fuseKeys = ['phone', 'email', 'name', 'pinNumber']

  const fuse = new Fuse(filteredAccountsByRole ?? [], extendFuseOptions(fuseKeys))

  const filteredAccounts = isEmpty(query)
    ? filteredAccountsByRole
    : fuse.search(query)?.map((account) => account.item)

  const paginator = paginationCoordinator<IPosAccountWithRoles>(
    filteredAccounts,
    location,
    navigate
  )
  const accountsFilteredAndPaginatedResult = paginator?.result

  const isLoading = isFetchingAccounts || isFetchingEmployeeRoles

  if (isLoading) {
    return <Loader />
  }

  return (
    <Layout
      mainContent={
        <PosAccountsLayout posAccounts={accountsFilteredAndPaginatedResult} paginator={paginator} />
      }
      navbarContent={
        <PosAccountsFilters
          query={query}
          setQuery={setQuery}
          handleChangeStatusSelect={setSelectStatusQuery}
          handleChangeRolesSelect={setRolesQuery}
        />
      }
    />
  )
}

export default PosAccounts
